import * as React from 'react';
import type { useTranslation } from 'react-i18next';
import { FITO_PRODUCTS } from '@insolech/catalogs';

/**
 * Reorder cart R2: the patient-facing FITO Model 1 stock-shoe "Add to your
 * order" box (box B). Purely additive - the parent renders it only when the
 * token's view carries fito_offered.
 *
 * FITO is a STOCK product (ships from stock, not custom-made) offered on both
 * anchor kinds. Price is server-set (fito_gross_aed, flat per pair) and framed
 * as an offer vs the retail reference. Colours black/grey/blue map to the F1
 * card codes; the server resolves + validates the exact sku (colour + size).
 *
 * The size run is NOT uniform across colours. Well Walking splits Model 1 by
 * colour: Black and Grey cover 35-48, Blue is 40-48 only, Lilac (not offered
 * here) is 35-42 only. Runs are read from FITO_PRODUCTS rather than
 * hard-coded, so this box can never offer a combination submit-reorder will
 * reject (it requires a matching Active catalog_skus row for
 * `${code}${size}`).
 *
 * SIZE IS A FIT CONSTRAINT, NOT A PREFERENCE. The size is prefilled from the
 * patient's original order and this flow has no podiatrist in the loop, so an
 * unavailable colour+size must REFUSE, never substitute. An earlier revision
 * snapped the size to the nearest one the colour is made in (37 + Blue -> 40);
 * that is silent substitution of a fit constraint and a patient could easily
 * miss that the number moved and receive a shoe that does not fit.
 *
 * So: the size select always offers the full run and is never rewritten by
 * this component. Colours the chosen size is not made in are DISABLED, with
 * an explicit message naming the colour and the size.
 */

const COLORS: Array<{
  id: 'black' | 'grey' | 'blue';
  code: string;
  swatch: string;
  image: string;
}> = [
  { id: 'black', code: 'F1BK', swatch: '#1f2937', image: '/products/fito-shoes/F1BK.jpg' },
  { id: 'grey', code: 'F1GY', swatch: '#9ca3af', image: '/products/fito-shoes/F1GY.jpg' },
  { id: 'blue', code: 'F1BU', swatch: '#2563eb', image: '/products/fito-shoes/F1BU.jpg' },
];

/** Numeric size labels for one F1 card code, e.g. ["40","41",…]. Reads the
 *  generated catalog so the manufacturer's real run is the single source. */
function sizesForCode(code: string): string[] {
  const product = FITO_PRODUCTS.find((p) => p.code === code);
  if (!product) return [];
  return product.sizes
    .map((s) => s.match(/^Size\s+(\d+)$/i)?.[1])
    .filter((s): s is string => !!s);
}

export const SIZES_BY_COLOR: Record<'black' | 'grey' | 'blue', string[]> = {
  black: sizesForCode('F1BK'),
  grey: sizesForCode('F1GY'),
  blue: sizesForCode('F1BU'),
};

/** Every size offered across the three colours. The select always shows the
 *  full run so the patient's real size stays selectable whatever colour is
 *  highlighted; availability is expressed on the COLOUR, not by trimming the
 *  size list out from under them. */
export const ALL_OFFERED_SIZES: string[] = [
  ...new Set(Object.values(SIZES_BY_COLOR).flat()),
].sort((a, b) => Number(a) - Number(b));

/** True when `color` is manufactured in `size`. Empty size = nothing chosen
 *  yet, so no colour is ruled out. */
export function colorHasSize(color: 'black' | 'grey' | 'blue', size: string): boolean {
  if (!size) return true;
  return SIZES_BY_COLOR[color].includes(size);
}

function formatAed(n: number): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

export function FitoBox({
  grossAed,
  retailAed,
  qty,
  setQty,
  color,
  setColor,
  size,
  setSize,
  maxQty,
  t,
}: {
  grossAed: number;
  retailAed: number | null;
  qty: number;
  setQty: (n: number) => void;
  color: 'black' | 'grey' | 'blue' | null;
  setColor: (c: 'black' | 'grey' | 'blue') => void;
  size: string;
  setSize: (s: string) => void;
  maxQty: number;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const pctOff =
    retailAed && retailAed > 0 && grossAed < retailAed
      ? Math.round((1 - grossAed / retailAed) * 100)
      : 0;
  const activeImage =
    (color ? COLORS.find((c) => c.id === color)?.image : undefined) ?? COLORS[0]!.image;
  // The chosen colour is unavailable when the patient's size is not made in
  // it. We never rewrite the size to make it fit.
  const colorUnavailable = !!color && !colorHasSize(color, size);
  // Cannot add to the cart until a colour is chosen (size is prefilled) and
  // that colour is actually manufactured in the chosen size.
  const canIncrease = !!color && !!size && !colorUnavailable;

  const colorLabel = (id: 'black' | 'grey' | 'blue'): string =>
    id === 'black'
      ? t('reorder_fito_color_black', 'Black')
      : id === 'grey'
        ? t('reorder_fito_color_grey', 'Grey')
        : t('reorder_fito_color_blue', 'Blue');

  return (
    <div
      className="rounded-xl border border-border-default bg-surface-raised p-4"
      data-testid="reorder-fito-box"
    >
      <p className="text-xs uppercase tracking-wider text-text-muted mb-2">
        {t('reorder_cross_box_title', 'Add to your order')}
      </p>
      <div className="flex items-start gap-3">
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md border border-border-subtle bg-white">
          <img src={activeImage} alt={t('reorder_fito_label', 'Fito Model 1 therapeutic shoes')} className="h-full w-full object-cover" loading="lazy" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{t('reorder_fito_label', 'Fito Model 1 therapeutic shoes')}</p>
          <p className="mt-0.5 text-sm" dir="ltr">
            <span className="font-bold text-brand-700">{formatAed(grossAed)} AED</span>
            <span className="ms-1 text-xs text-text-muted">
              {t('reorder_cross_per_pair', 'per pair, VAT included')}
            </span>
          </p>
          {retailAed && pctOff > 0 ? (
            <p className="mt-0.5 text-xs font-semibold text-success-700">
              {t('reorder_fito_offer', '{{pct}}% off vs the {{retail}} AED retail price', {
                pct: pctOff,
                retail: formatAed(retailAed),
              })}
            </p>
          ) : null}
          <p className="mt-1 text-xs text-text-muted">
            {t('reorder_fito_ships_from_stock', 'Ready-made, ships from stock.')}
          </p>

          {/* Colour swatches (required). */}
          <div className="mt-2">
            <p className="text-xs font-medium mb-1">{t('reorder_fito_colour', 'Colour')}</p>
            <div className="flex items-center gap-2" role="group" aria-label={t('reorder_fito_colour', 'Colour')}>
              {COLORS.map((c) => {
                // Disabled, not re-pointed: this colour simply is not made in
                // the size the patient needs.
                const available = colorHasSize(c.id, size);
                const label = available
                  ? colorLabel(c.id)
                  : t('reorder_fito_colour_not_in_size', '{{colour}} is not made in size {{size}}.', {
                      colour: colorLabel(c.id),
                      size,
                    });
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setColor(c.id)}
                    disabled={!available}
                    aria-pressed={color === c.id}
                    aria-label={label}
                    title={label}
                    className={`h-7 w-7 rounded-full border-2 ${
                      color === c.id ? 'border-brand-600' : 'border-border-subtle'
                    } ${
                      available
                        ? ''
                        : 'opacity-30 cursor-not-allowed grayscale'
                    }`}
                    style={{ backgroundColor: c.swatch }}
                    data-testid={`reorder-fito-color-${c.id}`}
                  />
                );
              })}
            </div>
          </div>

          {/* Size (prefilled, adjustable). */}
          <div className="mt-2 max-w-[10rem]">
            <label htmlFor="reorder-fito-size" className="text-xs font-medium">
              {t('reorder_fito_size', 'Size')}
            </label>
            <select
              id="reorder-fito-size"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              className="mt-1 block w-full rounded-md border border-border-default bg-surface-elevated px-2 py-1.5 text-sm"
              data-testid="reorder-fito-size"
            >
              {ALL_OFFERED_SIZES.map((s) => (
                <option key={s} value={s}>
                  {t('reorder_fito_size_option', 'Size {{n}}', { n: s })}
                </option>
              ))}
            </select>
          </div>

          {/* Quantity 0..max (0 = not added). */}
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setQty(Math.max(0, qty - 1))}
              disabled={qty <= 0}
              aria-label={t('reorder_cross_qty_decrease', 'Remove one pair')}
              className="h-8 w-8 rounded-md border border-border-default bg-surface-elevated text-lg leading-none disabled:opacity-40"
              data-testid="reorder-fito-qty-minus"
            >
              −
            </button>
            <span
              className="min-w-[2rem] text-center text-sm font-semibold tabular-nums"
              data-testid="reorder-fito-qty-value"
              aria-live="polite"
            >
              {qty}
            </span>
            <button
              type="button"
              onClick={() => setQty(Math.min(maxQty, qty + 1))}
              disabled={qty >= maxQty || !canIncrease}
              aria-label={t('reorder_cross_qty_increase', 'Add one pair')}
              className="h-8 w-8 rounded-md border border-border-default bg-surface-elevated text-lg leading-none disabled:opacity-40"
              data-testid="reorder-fito-qty-plus"
            >
              +
            </button>
            {qty > 0 ? (
              <span dir="ltr" className="ms-auto text-sm font-bold tabular-nums" data-testid="reorder-fito-line-total">
                {formatAed(Math.round(grossAed * qty * 100) / 100)} AED
              </span>
            ) : null}
          </div>
          {colorUnavailable ? (
            <p
              className="mt-1 text-xs font-medium text-error-700"
              role="status"
              data-testid="reorder-fito-colour-unavailable"
            >
              {t(
                'reorder_fito_colour_not_in_size',
                '{{colour}} is not made in size {{size}}.',
                { colour: colorLabel(color!), size },
              )}{' '}
              {t(
                'reorder_fito_choose_another',
                'Choose a different colour, or change the size.',
              )}
            </p>
          ) : !color ? (
            <p className="mt-1 text-xs text-text-muted">
              {t('reorder_fito_pick_colour_first', 'Choose a colour to add these to your order.')}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
